// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { UserInput, UserOrganisationInput, UserOutput } from 'thehive-sdk';
import { useLocalMessage } from '@Helpers/';
import { GlobalUserForm, GlobalUserValuesForm } from '@Organisms/User/GlobaUserCreate/GlobalUserForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';

interface GlobalUserCreateProps {
    onFinish: (user: UserInput, organisations: UserOrganisationInput[]) => void | Promise<void>;
    userList: UserOutput[];
    triggerElem?: ReactElement;
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

export const GlobalUserCreate = ({
    onFinish,
    userList,
    triggerElem,
    visible,
    onVisibleChange,
}: GlobalUserCreateProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const onFinishHandler = ({ login = '', name = '', organisations, type = 'Normal' }: GlobalUserValuesForm): void => {
        const { organisation, profile } = organisations.find(Boolean) || {};

        if (organisation && profile) {
            onFinish(
                {
                    login: login.trim(),
                    name: name.trim(),
                    organisation,
                    profile,
                    type,
                },
                organisations,
            );
        }
    };

    return (
        <THDrawer
            title={i('user.create.drawer.title')}
            triggerElem={triggerElem}
            className="drawer-form"
            afterVisibleChange={(visible): void => {
                form.resetFields();
                onVisibleChange?.(visible);
            }}
            disableClose={fieldsTouched}
            visible={visible}
        >
            {(toggleDrawer) => (
                <GlobalUserForm
                    onCancel={() => {
                        form.resetFields();
                        setFieldsTouched(false);
                        toggleDrawer();
                    }}
                    onFinish={(values) => {
                        onFinishHandler(values);
                        toggleDrawer();
                        setFieldsTouched(false);
                    }}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    form={form}
                    userList={userList}
                    isValid={isValid && fieldsTouched}
                />
            )}
        </THDrawer>
    );
};
